import get from 'lodash/get';

import { getDocument } from '@atlaskit/browser-apis';
import type { DatePickerBaseProps as DatePickerProps } from '@atlaskit/datetime-picker/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ReactSelectProps } from '@atlaskit/select/types';

import {
	DEFAULT_LOCALE,
	DEFAULT_WEEK_START_DAY,
	locales,
	longDateOnlyFormat,
	longTimeOnlyFormat,
} from '../constants';
import type { FieldId } from '../types/form/form-fields';
import type { FieldValues } from '../types/form/form-fields/field-values';

/**
 * Returns `true` when any field value *other than* `excludedFieldId` differs between two
 * `FieldValues` snapshots.
 *
 * Per-field value references in `FieldValues` are stable across store updates unless that
 * specific field changes (see `currentValuesSelector`), so a per-field reference check is a
 * reliable signal that an *external* dependency of an async field (e.g. a CMDB parent field)
 * changed and the field's option list may now be stale.
 *
 * The field's own value is excluded because selecting or clearing options within the field
 * itself cannot make its own option list stale - so callers can use this to avoid an
 * unnecessary refetch when only the field's own selection changed.
 */
export const haveOtherFieldValuesChanged = (
	previousValues: FieldValues,
	nextValues: FieldValues,
	excludedFieldId: FieldId,
): boolean => {
	if (previousValues === nextValues) {
		return false;
	}
	const previousKeys = Object.keys(previousValues).filter((id) => id !== excludedFieldId);
	const nextKeys = Object.keys(nextValues).filter((id) => id !== excludedFieldId);
	if (previousKeys.length !== nextKeys.length) {
		return true;
	}
	return nextKeys.some((id) => previousValues[id] !== nextValues[id]);
};

const IPAD_USER_AGENT = /iPad;/;
const IPHONE_USER_AGENT = /(iPhone;|iPhone Simulator;|iPod;|iPod touch;)/;

export const formatLocalTimeValueForView: (value: string | undefined, locale: string) => string = (
	value: string | undefined,
	locale: string,
): string => {
	if (!value || value.length === 0) {
		return '';
	}
	const timeSplit = value.split(':');
	const hours = parseInt(timeSplit[0], 10);
	const minutes = parseInt(timeSplit[1], 10);
	return new Intl.DateTimeFormat(locale, {
		...longTimeOnlyFormat,
		timeZone: 'UTC',
	}).format(Date.UTC(0, 0, 1, hours, minutes));
};

export const formatDateValueForView: (
	currentAnswer: string | undefined,
	locale: string,
) => string = (currentAnswer: string | undefined, locale: string): string => {
	if (!currentAnswer || currentAnswer.length === 0) {
		return '';
	}
	return new Intl.DateTimeFormat(locale, {
		...longDateOnlyFormat,
		timeZone: 'UTC',
	}).format(Date.parse(currentAnswer));
};

// Matches the ADS modal layer so a portalled menu still shows when the form is rendered in a modal.
const MENU_PORTAL_Z_INDEX = 510;

type DropDownMenuPositionProps = Pick<
	ReactSelectProps,
	'menuPosition' | 'menuPlacement' | 'menuPortalTarget'
> & {
	styles?: {
		menuPortal: <T extends object>(base: T) => T & { zIndex: number };
	};
};

export const getDropDownMenuPosition: () => DropDownMenuPositionProps =
	(): DropDownMenuPositionProps => {
		const menuPosition =
			isIphone() || isIpad()
				? // This is to prevent menu floating away due to virtual keyboard in iPad and iPhone
					'absolute'
				: 'fixed';

		const body = getDocument()?.body;
		if (!fg('proforma_select_menu_portal_to_body') || !body) {
			return { menuPosition };
		}

		return {
			menuPosition,
			// Each field is rendered inside an extension wrapper with `container-type: inline-size`, which
			// creates a stacking context on Safari <= 18.4 (e.g. iOS 17) and Chrome <= 128. Portalling the menu to
			// the body stops it painting behind the fields that follow it.
			menuPortalTarget: body,
			styles: { menuPortal: (base) => ({ ...base, zIndex: MENU_PORTAL_Z_INDEX }) },
		};
	};

const getLocale = (localeString: string): Locale => {
	const key = localeString as keyof typeof locales;
	return locales[key] || locales[DEFAULT_LOCALE as keyof typeof locales];
};

export const isIpad: () => boolean = (): boolean => {
	if (!navigator?.userAgent?.length || !navigator?.platform?.length) {
		return false;
	}
	const { userAgent, platform, maxTouchPoints } = navigator;

	// Check for iPad using user agent
	const isIpadUserAgent = IPAD_USER_AGENT.test(userAgent);

	// Check for iPad in desktop mode (iOS 13+)
	const isIpadDesktop = platform === 'MacIntel' && maxTouchPoints > 1;

	return isIpadUserAgent || isIpadDesktop;
};

export const isIphone: () => boolean = (): boolean => {
	if (!navigator?.userAgent?.length) {
		return false;
	}
	return IPHONE_USER_AGENT.test(navigator.userAgent);
};

export const parseDateTimeValueForView: (
	dateAnswer: string,
	timeAnswer: string,
	locale: string,
) => {
	formattedDateValue: string;
	formattedTimeValue: string;
} = (dateAnswer: string, timeAnswer: string, locale: string) => {
	const formattedDateValue = formatDateValueForView(dateAnswer, locale);
	const formattedTimeValue = formatLocalTimeValueForView(timeAnswer, locale);
	return { formattedDateValue, formattedTimeValue };
};

export const startOfWeekDay: (localeString: string) => DatePickerProps['weekStartDay'] = (
	localeString: string,
): DatePickerProps['weekStartDay'] => {
	const startWeekDay = get(getLocale(localeString), 'options.weekStartsOn', DEFAULT_WEEK_START_DAY);
	return startWeekDay as DatePickerProps['weekStartDay'];
};
